/**
 * Receipt-logo image processing, mirroring what the POS does at print time.
 *
 * The POS (ReceiptLogoCache, Dart) flattens transparency onto white, converts
 * to grayscale, fits the paper, then reduces to pure black and white —
 * thresholding flat artwork and dithering continuous tone. Reproducing that
 * here lets a merchant see the real printed result before committing, instead
 * of judging a colour logo and being surprised by the paper.
 *
 * Keep the constants and the luminance weights in step with the Dart side:
 * both use ITU-R 601 (0.299/0.587/0.114), matching the `image` package's
 * getLuminance.
 */

/** 80mm paper, the common roll. 58mm is 372 — same pipeline, narrower. */
export const PAPER_DOTS_80MM = 558;
export const PAPER_DOTS_58MM = 372;

/** Matches ReceiptLogoCache.maxHeightDots — ~25mm at 203dpi. */
export const MAX_HEIGHT_DOTS = 200;

/** Matches ReceiptLogoCache._flatArtMidToneLimit. */
const FLAT_ART_MIDTONE_LIMIT = 0.25;

/** Cap on what we store. The printer never needs more, and every register
 *  downloads this file on sync. */
const MAX_STORED_WIDTH = 1024;

export function loadImageFromFile(file: File): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read that image.'));
    };
    img.src = url;
  });
}

function newCanvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(w));
  canvas.height = Math.max(1, Math.round(h));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable in this browser.');
  return [canvas, ctx];
}

/** Draw source at w×h onto opaque white.
 *
 *  White, not transparent: thermal paper is white and the head only burns
 *  dots, so "no ink" has to mean white. Left transparent, those pixels read as
 *  luminance 0 and the logo prints as a solid black block. */
function drawOnWhite(
  src: CanvasImageSource,
  w: number,
  h: number,
  sx = 0,
  sy = 0,
  sw?: number,
  sh?: number,
): HTMLCanvasElement {
  const [canvas, ctx] = newCanvas(w, h);
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  if (sw !== undefined && sh !== undefined) {
    ctx.drawImage(src, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  } else {
    ctx.drawImage(src, 0, 0, canvas.width, canvas.height);
  }
  return canvas;
}

function luminance(r: number, g: number, b: number): number {
  return Math.round(0.299 * r + 0.587 * g + 0.114 * b);
}

/** Bounding box of everything meaningfully darker than the page. */
function inkBounds(img: HTMLImageElement) {
  const w = img.naturalWidth || 1;
  const h = img.naturalHeight || 1;
  const [, ctx] = [null, drawOnWhite(img, w, h).getContext('2d')] as [null, CanvasRenderingContext2D | null];
  if (!ctx) return { x: 0, y: 0, w, h };
  const d = ctx.getImageData(0, 0, w, h).data;
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const p = (y * w + x) * 4;
      if (luminance(d[p], d[p + 1], d[p + 2]) < 200) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return { x: 0, y: 0, w, h }; // blank image — leave it alone
  return { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 };
}

/** Scale factor that fits w×h to the paper. Never enlarges: upscaling a small
 *  logo only softens its edges, which then reduce to mush at 1 bit. */
function fitFactor(w: number, h: number, widthDots: number): number {
  return Math.min(widthDots / w, MAX_HEIGHT_DOTS / h, 1);
}

/** Share of pixels that are neither near-black ink nor near-white ground. */
function midToneFraction(data: Uint8ClampedArray): number {
  let mid = 0;
  const total = data.length / 4;
  if (!total) return 0;
  for (let p = 0; p < data.length; p += 4) {
    const l = luminance(data[p], data[p + 1], data[p + 2]);
    if (l > 40 && l < 215) mid++;
  }
  return mid / total;
}

/** True when the artwork is continuous tone (photo, gradient, heavy shading),
 *  which prints far worse on a thermal head than flat line art. */
export function isPhotographic(img: HTMLImageElement): boolean {
  const w = Math.min(img.naturalWidth || 1, 400);
  const scale = w / (img.naturalWidth || w);
  const h = Math.max(1, Math.round((img.naturalHeight || 1) * scale));
  const ctx = drawOnWhite(img, w, h).getContext('2d');
  if (!ctx) return false;
  return midToneFraction(ctx.getImageData(0, 0, w, h).data) >= FLAT_ART_MIDTONE_LIMIT;
}

/** Floyd–Steinberg error diffusion to pure black/white, in place. */
function ditherInPlace(data: Uint8ClampedArray, w: number, h: number) {
  const buf = new Float32Array(w * h);
  for (let i = 0, p = 0; i < buf.length; i++, p += 4) {
    buf[i] = luminance(data[p], data[p + 1], data[p + 2]);
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = y * w + x;
      const old = buf[i];
      const next = old < 128 ? 0 : 255;
      const err = old - next;
      buf[i] = next;
      if (x + 1 < w) buf[i + 1] += (err * 7) / 16;
      if (y + 1 < h) {
        if (x > 0) buf[i + w - 1] += (err * 3) / 16;
        buf[i + w] += (err * 5) / 16;
        if (x + 1 < w) buf[i + w + 1] += (err * 1) / 16;
      }
    }
  }
  for (let i = 0, p = 0; i < buf.length; i++, p += 4) {
    const v = buf[i] < 128 ? 0 : 255;
    data[p] = v;
    data[p + 1] = v;
    data[p + 2] = v;
    data[p + 3] = 255;
  }
}

/** Hard cut at mid grey, in place. */
function thresholdInPlace(data: Uint8ClampedArray) {
  for (let p = 0; p < data.length; p += 4) {
    const v = luminance(data[p], data[p + 1], data[p + 2]) < 128 ? 0 : 255;
    data[p] = v;
    data[p + 1] = v;
    data[p + 2] = v;
    data[p + 3] = 255;
  }
}

/**
 * Reduce to black and white by whichever method suits the artwork.
 *
 * Dithering exists for continuous tone. Applied to a flat logo it is actively
 * harmful — error diffusion turns solid strokes into stipple and scatters
 * isolated dots across empty margins, which reads as specks all over the
 * receipt. The POS makes the same choice on the same measure.
 */
function reduceInPlace(data: Uint8ClampedArray, w: number, h: number) {
  if (midToneFraction(data) < FLAT_ART_MIDTONE_LIMIT) thresholdInPlace(data);
  else ditherInPlace(data, w, h);
}

/** The logo exactly as it will print on [widthDots] paper, margins trimmed. */
export function renderPrintPreview(img: HTMLImageElement, widthDots: number): HTMLCanvasElement {
  const box = inkBounds(img);
  const factor = fitFactor(box.w, box.h, widthDots);
  const canvas = drawOnWhite(img, box.w * factor, box.h * factor, box.x, box.y, box.w, box.h);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable in this browser.');
  const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
  reduceInPlace(frame.data, canvas.width, canvas.height);
  ctx.putImageData(frame, 0, 0);
  return canvas;
}

/**
 * The bytes we store: trimmed to the artwork, flattened onto white and
 * grayscaled — but NOT reduced to 1 bit, and at a higher resolution than any
 * single paper width.
 *
 * Trimming is the single biggest print-quality win. A logo exported with
 * generous whitespace wastes the height cap on empty margin; cropping to the
 * ink lets the artwork itself fill the space the receipt allows.
 *
 * The 1-bit step is deliberately left to the POS: a dither pattern does not
 * survive rescaling, so the register reduces once, at the width of the paper
 * actually loaded.
 */
export function toGrayscalePng(img: HTMLImageElement): Promise<Blob> {
  const box = inkBounds(img);
  const scale = Math.min(MAX_STORED_WIDTH / box.w, 1);
  const canvas = drawOnWhite(img, box.w * scale, box.h * scale, box.x, box.y, box.w, box.h);
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas is unavailable in this browser.');
  const frame = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const d = frame.data;
  for (let p = 0; p < d.length; p += 4) {
    const v = luminance(d[p], d[p + 1], d[p + 2]);
    d[p] = v;
    d[p + 1] = v;
    d[p + 2] = v;
    d[p + 3] = 255;
  }
  ctx.putImageData(frame, 0, 0);
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the image.'))), 'image/png');
  });
}
